/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import firebase from 'firebase/compat/app';
import { CommonModule, Location } from '@angular/common';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { BehaviorSubject, Observable, Subject, distinctUntilChanged } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import * as appStrings from '@touchplan/strings';
import { Tenant, User } from '@touchplan/common';
import { ReactiveFormsModule } from '@angular/forms';
import { DefaultLoggerService } from '@touchplan/logging';
import { tenantGetterService } from '@touchplan/firebase-common';

import { AuthService } from 'src/app/services/auth.service';
import { AppState } from 'src/app/services/app-state.service';
import { MaterialModule } from 'src/app/common/material/material-module';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { ParentErrorStateMatcher } from 'src/app/utils/parent-error-state-matcher';
import { ITitleField, titleFields } from 'src/app/app-data/constants/title-fields';
import { BannerMessageHandler } from 'src/app/common/logging/banner-message-handler';
import { SharedUIMaterialModule } from 'src/app/shared/ui/material/shared-ui-material.module';
import { PresentationModule } from 'src/app/common/presentation-components/presentation.module';
import {
	emailFormatValidator,
	passwordFormatValidator,
	passwordMatchValidator,
} from 'src/app/common/form-utils/validators';
import { PasswordPolicyListComponent } from 'src/app/common/auth/components/password-policy-list/password-policy-list.component';
import { fancyDatabase } from '@touchplan/client-firebase';

interface UserWithUpdate extends User {
	update: (data: any) => void;
}

const COMPONENT_IMPORTS = [
	CommonModule,
	MaterialModule,
	PresentationModule,
	ReactiveFormsModule,
	SharedUIMaterialModule,
	PasswordPolicyListComponent,
];
@Component({
	standalone: true,
	imports: [...COMPONENT_IMPORTS],
	selector: 'account-profile',
	templateUrl: './account-profile.component.html',
	styleUrls: ['account-profile.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [LoggerServiceFactory.provide('AccountProfileComponent')],
})
export class AccountProfileComponent implements OnInit, OnDestroy {
	public appStrings = appStrings;
	private shutdownSubject = new Subject<void>();
	public user: UserWithUpdate;
	public isPasswordFieldActive = false;
	public titleFields$ = new BehaviorSubject<Map<string, ITitleField>>(new Map());
	public newPasswordErrorMatcher = new ParentErrorStateMatcher();
	public confirmPasswordErrorMatcher = new ParentErrorStateMatcher(true);
	public isSSO$ = this.authService.isUserSSO();

	get userData() {
		return this.userForm.get('userData') as UntypedFormGroup;
	}

	get passwords() {
		return this.userForm.get('passwords') as UntypedFormGroup;
	}

	get email() {
		return this.userData.get('email');
	}

	get currentPassword() {
		return this.passwords.get('currentPassword');
	}

	get newPassword() {
		return this.passwords.get('newPassword');
	}

	get confirmPassword() {
		return this.passwords.get('confirmPassword');
	}

	public userForm = this.formBuilder.group({
		userData: this.formBuilder.group({
			email: [{ value: '', disabled: true }, emailFormatValidator()],
			firstName: ['', Validators.required],
			lastName: ['', Validators.required],
			company: ['', Validators.required],
			phone: ['', Validators.required],
			title: ['', Validators.required],
			zip: ['', Validators.required],
		}),
		passwords: this.formBuilder.group(
			{
				currentPassword: [''], // Validators set dynamically
				newPassword: [''], // Validators set dynamically
				confirmPassword: [''], // Validators set dynamically
			},
			{ validators: passwordMatchValidator('newPassword', 'confirmPassword') }
		),
	});

	public showChangePasswordForm$: Observable<boolean> = this.angularFireAuth.authState.pipe(
		filter((userExists) => !!userExists),
		takeUntil(this.shutdownSubject),
		switchMap((firebaseUser) => tenantGetterService.getTenantViaAuthId(fancyDatabase, firebaseUser.tenantId)),
		// If no tenant or tenant doesn't include metadata or tenant doesn't include providers assume it is the default tenant.
		map((tenant) =>
			!tenant || !tenant.tenantMetadata || !tenant.tenantMetadata.providers
				? Tenant.getDefaultTenant()
				: tenant
		),
		map((tenant) => Object.keys(tenant.tenantMetadata.providers).includes('password'))
	);

	public onSubmit() {
		this.logger.log('form', this.userForm.value);

		if (!this.user) {
			this.logger.log('user not defined on submit');
			return;
		}

		if (this.userForm.invalid) {
			this.logger.log('form invalid');
			return;
		}

		try {
			if (!this.arePasswordFieldsEmpty()) {
				this.changePassword();
			}
			this.user.update(this.userData.value);
			this.bannerMessageHandler.displaySuccessMessage(appStrings.PROFILE_UPDATE_SUCCESS);
		} catch (error) {
			if (error instanceof Error) {
				this.bannerMessageHandler.displayErrorMessage(error, appStrings.PROFILE_UPDATE_ERROR);
				this.logger.error(error);
			} else {
				this.logger.error('unknown error', error);
			}
		}
	}

	public goBack() {
		this.location.back();
	}

	private setPasswordFieldsValidators(): void {
		if (this.arePasswordFieldsEmpty()) {
			this.currentPassword.clearValidators();
			this.newPassword.clearValidators();
			this.confirmPassword.clearValidators();
		} else {
			this.currentPassword.setValidators([Validators.required]);
			this.newPassword.setValidators([
				Validators.required,
				Validators.minLength(10),
				passwordFormatValidator(),
			]);
			this.confirmPassword.setValidators([Validators.required]);
		}
		this.currentPassword.updateValueAndValidity();
		this.newPassword.updateValueAndValidity();
		this.confirmPassword.updateValueAndValidity();
	}

	private mapTitleFields(): void {
		const titleFieldsMap = new Map();
		titleFields.forEach((field) => titleFieldsMap.set(field.label, field));

		this.titleFields$.next(titleFieldsMap);
	}

	private changePassword(): void {
		const credential = firebase.auth.EmailAuthProvider.credential(
			this.email.value,
			this.currentPassword.value
		);
		const authUser = firebase.auth().currentUser;
		authUser
			.reauthenticateWithCredential(credential)
			.then(() => {
				return authUser.updatePassword(this.newPassword.value);
			})
			.then(() => {
				this.bannerMessageHandler.displaySuccessMessage(appStrings.PROFILE_UPDATE_PASSWORD_SUCCESS);
			})
			.catch((error) => {
				this.bannerMessageHandler.displayErrorMessage(error, appStrings.PROFILE_UPDATE_PASSWORD_ERROR);
			})
			.finally(() => {
				this.cleanPasswordFields();
			});
	}

	private loadUser(): void {
		this.appState.user
			.pipe(
				filter((user) => !!user),
				takeUntil(this.shutdownSubject)
			)
			.subscribe((user) => {
				this.user = user as any;
				this.userData.patchValue(user);
			});
	}

	private arePasswordFieldsEmpty(): boolean {
		return (
			this.currentPassword.value === '' && this.newPassword.value === '' && this.confirmPassword.value === ''
		);
	}

	private cleanPasswordFields(): void {
		this.currentPassword.setValue('');
		this.newPassword.setValue('');
		this.confirmPassword.setValue('');
		this.setPasswordFieldsValidators();
	}

	private listenPasswordsChanges(): void {
		this.passwords.valueChanges
			.pipe(
				map(() => this.arePasswordFieldsEmpty()),
				distinctUntilChanged(),
				takeUntil(this.shutdownSubject)
			)
			.subscribe(() => {
				this.setPasswordFieldsValidators();
			});
	}

	public ngOnDestroy() {
		this.shutdownSubject.next();
	}

	public ngOnInit() {
		this.mapTitleFields();
		this.loadUser();
		this.listenPasswordsChanges();
	}

	constructor(
		public appState: AppState,
		private location: Location,
		private authService: AuthService,
		private logger: DefaultLoggerService,
		private formBuilder: UntypedFormBuilder,
		private angularFireAuth: AngularFireAuth,
		private bannerMessageHandler: BannerMessageHandler
	) {}
}
